import type { WhatsappTemplateParams, WhatsappTemplateSummary } from '../api/types';

/**
 * Filling in a WhatsApp template. Meta rebuilds the approved template from the
 * components a send supplies and rejects the whole message when they don't line up,
 * so a template with a media header or a dynamic URL button needs those values
 * collected too — a body-only send of such a template always fails.
 */
export interface WhatsappTemplateDraft {
  body: string[];
  headerText: string[];
  headerMediaUrl: string;
  buttons: string[];
}

const MEDIA_HEADERS = ['IMAGE', 'VIDEO', 'DOCUMENT'];

/**
 * How a variable is named to the agent. Positional placeholders mean nothing on
 * their own, so they get a label; named ones already read as one. Shared with the
 * form so "still needed" names the field the agent is looking at.
 */
export const variableLabel = (name: string) => (/^\d+$/.test(name) ? `Variable ${name}` : name);

export const headerNeedsMedia = (summary: WhatsappTemplateSummary) =>
  !!summary.header && MEDIA_HEADERS.includes(summary.header.format);

/** Text variables in the header — none when the header is a file or a pin. */
export const headerTextParams = (summary: WhatsappTemplateSummary) =>
  headerNeedsMedia(summary) ? [] : (summary.header?.params ?? []);

export function emptyDraft(summary: WhatsappTemplateSummary): WhatsappTemplateDraft {
  return {
    body: summary.bodyParams.map(() => ''),
    headerText: headerTextParams(summary).map(() => ''),
    headerMediaUrl: '',
    buttons: summary.buttons.map(() => ''),
  };
}

/** Human-readable names of everything still blank — empty means the send is ready. */
export function missingFields(
  summary: WhatsappTemplateSummary,
  draft: WhatsappTemplateDraft,
): string[] {
  const missing: string[] = [];
  summary.bodyParams.forEach((name, index) => {
    if (!draft.body[index]?.trim()) missing.push(variableLabel(name));
  });
  headerTextParams(summary).forEach((name, index) => {
    if (!draft.headerText[index]?.trim()) missing.push(`Header ${variableLabel(name)}`);
  });
  if (headerNeedsMedia(summary) && !draft.headerMediaUrl.trim()) missing.push('Header file');
  summary.buttons.forEach((button, index) => {
    if (button.expectsParam && !draft.buttons[index]?.trim()) {
      missing.push(`Button “${button.text || button.type}”`);
    }
  });
  return missing;
}

const substitute = (text: string, names: string[], values: string[]) =>
  text.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (match, token: string) => {
    const index = names.indexOf(token);
    const value = index === -1 ? '' : values[index];
    return value?.trim() ? value : match;
  });

/** The body as the contact will read it, for the preview and nothing else. */
export const renderPreview = (summary: WhatsappTemplateSummary, draft: WhatsappTemplateDraft) =>
  substitute(summary.bodyText, summary.bodyParams, draft.body);

export function toTemplateParams(
  summary: WhatsappTemplateSummary,
  draft: WhatsappTemplateDraft,
): WhatsappTemplateParams {
  const buttons = summary.buttons
    .map((button, index) => ({ button, parameter: draft.buttons[index] ?? '' }))
    // A parameter sent for a button that takes none is itself a rejection, so only
    // the buttons the template declared as dynamic travel with the send.
    .filter(({ button, parameter }) => button.expectsParam && parameter.trim())
    .map(({ button, parameter }) => ({
      index: button.index,
      type: button.type.toLowerCase(),
      parameter: parameter.trim(),
    }));

  const header = headerNeedsMedia(summary)
    ? {
        mediaUrl: draft.headerMediaUrl.trim(),
        mediaType: summary.header?.format.toLowerCase(),
      }
    : draft.headerText.length
      ? { text: draft.headerText.map((value) => value.trim()) }
      : undefined;

  return {
    name: summary.name,
    language: summary.language,
    ...(summary.bodyParams.length ? { body: draft.body.map((value) => value.trim()) } : {}),
    ...(header ? { header } : {}),
    ...(buttons.length ? { buttons } : {}),
  };
}
